import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import {
  getAssignedSeedBookingOrders,
  getSeedBookingErrorMessage,
  getSeedBookingOrders,
} from "./SeedBookingApi";
import {
  EmptyState,
  LoadingState,
  PageHeader,
  StatusBadge,
  formatAmount,
  formatDate,
} from "./SeedBookingUi";
import "./SeedBooking.css";

const STATUS_FILTERS = [
  { value: "All", label: "All" },
  { value: "PendingForReview", label: "Pending" },
  { value: "ApprovedBySalesTeam", label: "Approved" },
  { value: "Rejected", label: "Rejected" },
];

const SeedBookingOrderList = () => {
  const { distributorId: routeDistributorId } = useParams();
  const location = useLocation();
  const isBpUser = localStorage.getItem("userType") === "BPUser";
  const distributorId = routeDistributorId
    || (!isBpUser ? localStorage.getItem("distributorId") || "" : "");
  const canStartBooking = isBpUser ? Boolean(routeDistributorId) : Boolean(distributorId);
  const detailsPath = isBpUser
    ? "/BPUDashboard/SeedBookingOrderDetails"
    : "/SeedBookingOrderDetails";
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!distributorId && !isBpUser) {
      setLoading(false);
      setOrders([]);
      return undefined;
    }

    let active = true;
    setLoading(true);
    const ordersRequest = distributorId
      ? getSeedBookingOrders(distributorId)
      : getAssignedSeedBookingOrders();
    ordersRequest
      .then((result) => active && setOrders(result || []))
      .catch((error) => active && toast.error(getSeedBookingErrorMessage(error)))
      .finally(() => active && setLoading(false));

    return () => {
      active = false;
    };
  }, [distributorId, isBpUser]);

  const showDistributor = isBpUser && !routeDistributorId;
  const headerDistributorName = isBpUser && routeDistributorId && orders.length
    ? orders[0].distributorDisplayName
    : "";
  const countByStatus = (status) =>
    status === "All" ? orders.length : orders.filter((order) => order.status === status).length;
  const search = searchTerm.trim().toLocaleLowerCase();
  const visibleOrders = orders.filter((order) =>
    (statusFilter === "All" || order.status === statusFilter)
    && (!search
      || order.seedBookingOrderNumber.toLocaleLowerCase().includes(search)
      || (order.distributorDisplayName || "").toLocaleLowerCase().includes(search)),
  );

  return (
    <section className="content seed-booking-page">
      <div className="container-fluid">
        <PageHeader
          icon="fa-list-alt"
          title="Seed Booking Orders"
          subtitle={showDistributor
            ? "Bookings from all distributors assigned to you."
            : "Track submitted seed bookings and their approval status."}
          distributorName={headerDistributorName}
        >
          {!isBpUser && canStartBooking && (
            <Link
              to="/SeedBooking"
              className="btn sb-btn sb-btn-primary"
              onClick={() => localStorage.setItem("distributorId", distributorId)}
            >
              <i className="fa fa-plus" aria-hidden="true"></i> New booking
            </Link>
          )}
        </PageHeader>

        {!distributorId && !isBpUser ? (
          <EmptyState icon="fa-user-circle" title="No distributor">
            No distributor is associated with this account.
          </EmptyState>
        ) : loading ? (
          <LoadingState label="Loading orders..." />
        ) : orders.length === 0 ? (
          <EmptyState icon="fa-inbox" title="No bookings yet">
            Submitted seed bookings will appear here.
            {!isBpUser && canStartBooking && (
              <Link to="/SeedBooking" className="btn sb-btn sb-btn-primary">Start a booking</Link>
            )}
          </EmptyState>
        ) : (
          <>
            <div className="sb-toolbar">
              <div className="sb-tabs" role="tablist" aria-label="Filter by status">
                {STATUS_FILTERS.map((filter) => (
                  <button
                    key={filter.value}
                    type="button"
                    role="tab"
                    aria-selected={statusFilter === filter.value}
                    className={`sb-tab${statusFilter === filter.value ? " is-active" : ""}`}
                    onClick={() => setStatusFilter(filter.value)}
                  >
                    {filter.label}
                    <span className="sb-tab-count">{countByStatus(filter.value)}</span>
                  </button>
                ))}
              </div>
              <div className="sb-search">
                <i className="fa fa-search" aria-hidden="true"></i>
                <input
                  type="search"
                  className="form-control"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={showDistributor ? "Order number or distributor" : "Order number"}
                  aria-label="Search orders"
                />
              </div>
            </div>

            {visibleOrders.length === 0 ? (
              <EmptyState icon="fa-filter" title="No matching orders">
                Try a different status or search term.
              </EmptyState>
            ) : (
              <ul className="sb-orders">
                {visibleOrders.map((order) => (
                  <li key={order.id}>
                    <Link
                      to={`${detailsPath}/${order.id}`}
                      state={{ ordersPath: location.pathname }}
                      className="sb-card sb-order"
                    >
                      <div className="sb-order-main">
                        {showDistributor && (
                          <span className="sb-order-distributor">{order.distributorDisplayName}</span>
                        )}
                        <span className="sb-order-number">{order.seedBookingOrderNumber}</span>
                        <span className="sb-order-meta">
                          <i className="fa fa-calendar-o" aria-hidden="true"></i> {formatDate(order.createdOn)}
                          <span className="sb-dot" aria-hidden="true">·</span>
                          {order.itemCount} {order.itemCount === 1 ? "item" : "items"}
                        </span>
                      </div>
                      <div className="sb-order-side">
                        <span className="sb-order-amount">{formatAmount(order.subTotal)}</span>
                        <StatusBadge status={order.status} />
                      </div>
                      <i className="fa fa-chevron-right sb-order-chevron" aria-hidden="true"></i>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
      <ToastContainer position="bottom-center" autoClose={3500} limit={2} />
    </section>
  );
};

export default SeedBookingOrderList;
